/**
 * ParticipantsDrawer — gestão dos participantes de um Bond (papel + permissões =
 * ACL, ADR-130), surface na aba Cliente da conversa. 3º e último sub-recurso do
 * trio do Bond (participants / related-persons / authorized-contacts). Espelha o
 * B2 RelatedPersonsDrawer, com os campos extras do participante (XOR pessoa/usuário,
 * permissões multi-select, escopo, lado, primário).
 *
 * Gate `bond-basic` aplicado no chamador (TabCliente) + server-side.
 */

import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import { useAddParticipant, useManagedParticipants } from '../../../hooks/use-bond-participants';

import './participants.css';
import { ParticipantCard } from './ParticipantCard';
import {
  buildAddRequest,
  initialAddForm,
  togglePermission,
  validateAddForm,
  type ParticipantFormState,
} from './participants-form';
import { PERMISSION_SCOPE_OPTIONS, SIDE_OPTIONS } from './participants-options';
import { PermissionPicker } from './PermissionPicker';
import { RoleField } from './RoleField';

/**
 * Conteúdo (sem o Drawer) — reutilizável pelo ParticipantsDrawer (aba Cliente)
 * e pelo BondManagementDrawer com abas (ficha da Pessoa). `bondId` já resolvido.
 */
export function ParticipantsPanel({ bondId }: { bondId: string }): JSX.Element {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [adding, setAdding] = useState(false);

  const { data: participants, isLoading, isError } = useManagedParticipants(bondId, includeRevoked);

  return (
    <div className="bpt-scope">
      <p className="bpt-intro">
        Quem tem papel operacional e permissões neste vínculo (ACL · ADR-130) — pessoas externas ou
        usuários internos. Diferente de pessoas relacionadas (vínculo factual) e contatos
        autorizados (autorização LGPD): aqui se define acesso e autoridade.
      </p>

      <div className="bpt-toolbar">
        <label className="bpt-toggle">
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
          />
          <span>Incluir revogados</span>
        </label>
        {!adding && (
          <button
            type="button"
            className="bpt-btn bpt-btn--primary"
            onClick={() => setAdding(true)}
          >
            + Adicionar participante
          </button>
        )}
      </div>

      {adding && <AddParticipantForm bondId={bondId} onDone={() => setAdding(false)} />}

      {isLoading ? (
        <p className="bpt-muted">Carregando participantes…</p>
      ) : isError ? (
        <p className="bpt-alert bpt-alert--error" role="alert">
          Não foi possível carregar os participantes.
        </p>
      ) : participants.length === 0 ? (
        <p className="bpt-empty">
          Nenhum participante{includeRevoked ? '' : ' ativo'} neste vínculo.
        </p>
      ) : (
        <div className="bpt-list">
          {participants.map((p) => (
            <ParticipantCard key={p.id} bondId={p.bondId} participant={p} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ParticipantsDrawer({
  bondId,
  onClose,
}: {
  bondId: string | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Drawer isOpen={bondId !== null} onClose={onClose} title="Participantes do vínculo" width="lg">
      {bondId !== null && <ParticipantsPanel bondId={bondId} />}
    </Drawer>
  );
}

function AddParticipantForm({
  bondId,
  onDone,
}: {
  bondId: string;
  onDone: () => void;
}): JSX.Element {
  const add = useAddParticipant(bondId);
  const [form, setForm] = useState<ParticipantFormState>(initialAddForm);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(): void {
    const v = validateAddForm(form);
    if (v !== null) {
      setError(v);
      return;
    }
    setError(null);
    add.mutate(buildAddRequest(form), {
      onSuccess: onDone,
      onError: (err) => setError(err.message !== '' ? err.message : 'Falha ao adicionar.'),
    });
  }

  return (
    <div className="bpt-inline bpt-inline--add">
      <div className="bpt-field">
        <span className="bpt-field__label">Tipo de participante</span>
        <div className="bpt-segment">
          <button
            type="button"
            className={`bpt-segment__btn${form.subjectKind === 'person' ? ' bpt-segment__btn--on' : ''}`}
            onClick={() => setForm((p) => ({ ...p, subjectKind: 'person' }))}
          >
            Pessoa externa
          </button>
          <button
            type="button"
            className={`bpt-segment__btn${form.subjectKind === 'user' ? ' bpt-segment__btn--on' : ''}`}
            onClick={() => setForm((p) => ({ ...p, subjectKind: 'user' }))}
          >
            Usuário interno
          </button>
        </div>
      </div>

      <label className="bpt-field" htmlFor="add-subject-id">
        <span className="bpt-field__label">
          {form.subjectKind === 'person' ? 'ID da pessoa' : 'ID do usuário interno'}
          <span className="bpt-defer-tag">picker diferido</span>
        </span>
        <input
          id="add-subject-id"
          className="bpt-input"
          placeholder={form.subjectKind === 'person' ? 'person_…' : 'user_…'}
          value={form.subjectKind === 'person' ? form.personId : form.userId}
          onChange={(e) =>
            setForm((p) =>
              p.subjectKind === 'person'
                ? { ...p, personId: e.target.value }
                : { ...p, userId: e.target.value },
            )
          }
        />
      </label>

      <RoleField
        id="add-role"
        value={form.role}
        onChange={(role) => setForm((p) => ({ ...p, role }))}
      />

      <div className="bpt-field">
        <span className="bpt-field__label">Permissões</span>
        <PermissionPicker
          selected={form.permissions}
          onToggle={(k) =>
            setForm((p) => ({ ...p, permissions: togglePermission(p.permissions, k) }))
          }
          idPrefix="add"
        />
      </div>

      <label className="bpt-field" htmlFor="add-scope">
        <span className="bpt-field__label">Escopo</span>
        <select
          id="add-scope"
          className="bpt-input bpt-select"
          value={form.permissionScope}
          onChange={(e) => setForm((p) => ({ ...p, permissionScope: e.target.value }))}
        >
          {PERMISSION_SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="bpt-field" htmlFor="add-side">
        <span className="bpt-field__label">Lado (opcional)</span>
        <select
          id="add-side"
          className="bpt-input bpt-select"
          value={form.side}
          onChange={(e) => setForm((p) => ({ ...p, side: e.target.value }))}
        >
          {SIDE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="bpt-checkline" htmlFor="add-primary">
        <input
          id="add-primary"
          type="checkbox"
          checked={form.isPrimary}
          onChange={(e) => setForm((p) => ({ ...p, isPrimary: e.target.checked }))}
        />
        <span>Participante principal</span>
      </label>

      {error !== null && (
        <p className="bpt-alert bpt-alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="bpt-inline__actions">
        <button type="button" className="bpt-btn bpt-btn--ghost" onClick={onDone}>
          Cancelar
        </button>
        <button
          type="button"
          className="bpt-btn bpt-btn--primary"
          disabled={add.isPending}
          onClick={handleSubmit}
        >
          {add.isPending ? 'Adicionando…' : 'Adicionar participante'}
        </button>
      </div>
    </div>
  );
}
